import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { useDispatch } from "redux-react-hook";
import * as yup from "yup";
import styled from "styled-components";

import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useProductsApi,
  IProductCategory
} from "src/core/products/api/products.api";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { PageProps } from "src/pages/Router/routes";

type UrlParams = {
  categoryId: string;
};

const updateCategoryForm: (productCategory: IProductCategory, productCategories: IProductCategory[]) => IFormField[] = (
  productCategory,
  productCategories
) => {
  const fields: IFormField[] = [
    {
      type: "input",
      name: "name",
      label: "Category name",
      placeholder: "Category name",
      value: productCategory.name,
      validation: yup
        .string()
        .min(1)
        .required("Category name is required")
    }
  ];

  if (!_.find(productCategories, cat => cat.parentId === productCategory.id) || productCategory.parentId) {
    fields.push({
      type: "select",
      name: "parentId",
      label: "Parent category",
      value: productCategory.parentId ? productCategory.parentId.toString() : undefined,
      options: _.map(productCategories.filter(category => !category.parentId && category.id !== productCategory.id), category => ({
        value: category.id.toString(),
        text: category.name,
        checked: productCategory.parentId && productCategory.parentId === category.id ? true : false
      })),
      validation: yup.string().oneOf(_.concat(_.map(productCategories, category => category.id.toString()), [ "" ]))
    });
  }

  return fields;
};

const ProductCategoryPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const {
    history, terminology, match
  } = props;

  const categoryId = match ? parseInt(match.params.categoryId, 0) : 0;
  const [ listCategoriesResponse, getProductCategories ] = useProductsApi("LIST_PRODUCT_CATEGORIES");
  const [ updateCategoryResponse, updateProductCategory ] = useProductsApi("UPDATE_PRODUCT_CATEGORY");
  const [ productCategory, setProductCategory ] = React.useState<IProductCategory>();
  const dispatch = useDispatch();

  React.useEffect(() => {
    getProductCategories({
      filters: {},
      branchId: null
    });
  }, []);

  React.useEffect(() => {
    if (listCategoriesResponse.data) {
      const found = _.find(listCategoriesResponse.data, cat => {
        return cat.id === categoryId;
      });

      if (found) {
        setProductCategory(found);
      }
    }

    if (listCategoriesResponse.error) {
      dispatch(setGlobalError(listCategoriesResponse.error));
    }
  }, [ listCategoriesResponse ]);

  React.useEffect(() => {
    if (updateCategoryResponse.data) {
      history.push("/admin/products/categories");
    }
  }, [ updateCategoryResponse ]);
  const formRef = React.useRef<Formik>(null);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <NewCategoryWrapper>
          <Text
            fontSize="h3"
            weight="bold"
          >
            {`Update ${terminology.product.singular.text} ${terminology.category.singular.text}`}
          </Text>

          {listCategoriesResponse.data && productCategory && (
            <FormWrapper>
              <Form
                ref={formRef}
                error={updateCategoryResponse.error ? updateCategoryResponse.error.message : null}
                submitting={updateCategoryResponse.pending}
                fields={updateCategoryForm(productCategory, listCategoriesResponse.data)}
                onSubmit={({ name, parentId }) =>
                  updateProductCategory({
                    id: productCategory ? productCategory.id : 0,
                    name,
                    parentId: parentId && parentId.length ? parentId : null,
                    sortOrder: productCategory.sortOrder
                  })}
                submitButtonText="Update"
              />
            </FormWrapper>
          )}
        </NewCategoryWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  form {
    min-width: 350px;
  }
`;

const NewCategoryWrapper = styled(Grid.Box)`
  padding: 0 15px;
`;

export default ProductCategoryPage;
